import { supabase } from '../supabase/supabaseClient';

/**
 * Terms and Conditions Service
 * Handles terms acceptance tracking in database for legal compliance
 */
class TermsService {
  constructor() {
    this.currentVersion = '1.0'; // Update this when terms change
    this.isEnabled = this.checkSupabaseConfig();
    
    if (this.isEnabled) {
    } else {
      console.warn('⚠️ Terms Service disabled - using localStorage fallback');
    }
  }

  checkSupabaseConfig() {
    try {
      return !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_KEY);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get user's IP address for audit trail (optional)
   */
  async getUserIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Could not fetch IP address:', error);
      return null;
    }
  }

  /**
   * Get user agent for audit trail
   */
  getUserAgent() {
    return navigator.userAgent;
  }

  /**
   * Accept terms for a clinician or superadmin user
   */
  async acceptUserTerms(userId) {
    if (!this.isEnabled) {
      // Fallback to localStorage
      localStorage.setItem('terms_accepted_clinician', 'true');
      return { success: true, fallback: true };
    }

    try {
      const ipAddress = await this.getUserIP();
      const userAgent = this.getUserAgent();

      const { data, error } = await supabase.rpc('accept_user_terms', {
        p_user_id: userId,
        p_terms_version: this.currentVersion,
        p_ip_address: ipAddress,
        p_user_agent: userAgent
      });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error accepting user terms:', error);
      // Fallback to localStorage on error
      localStorage.setItem('terms_accepted_clinician', 'true');
      return { success: false, error, fallback: true };
    }
  }

  /**
   * Accept terms for a patient
   */
  async acceptPatientTerms(patientId) {
    if (!this.isEnabled) {
      // Fallback to localStorage
      localStorage.setItem('terms_accepted_patient', 'true');
      return { success: true, fallback: true };
    }

    try {
      const ipAddress = await this.getUserIP();
      const userAgent = this.getUserAgent();

      const { data, error } = await supabase.rpc('accept_patient_terms', {
        p_patient_id: patientId,
        p_terms_version: this.currentVersion,
        p_ip_address: ipAddress,
        p_user_agent: userAgent
      });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error accepting patient terms:', error);
      // Fallback to localStorage on error
      localStorage.setItem('terms_accepted_patient', 'true');
      return { success: false, error, fallback: true };
    }
  }

  /**
   * Check if user has accepted terms
   */
  async checkUserTermsAcceptance(userId) {
    if (!this.isEnabled) {
      // Fallback to localStorage
      return localStorage.getItem('terms_accepted_clinician') === 'true';
    }

    try {
      const { data, error } = await supabase.rpc('check_user_terms_acceptance', {
        p_user_id: userId
      });

      if (error) throw error;

      return data === true;
    } catch (error) {
      console.error('Error checking user terms acceptance:', error);
      // Fallback to localStorage on error
      return localStorage.getItem('terms_accepted_clinician') === 'true';
    }
  }

  /**
   * Check if patient has accepted terms
   */
  async checkPatientTermsAcceptance(patientId) {
    if (!this.isEnabled) {
      // Fallback to localStorage
      return localStorage.getItem('terms_accepted_patient') === 'true';
    }

    try {
      const { data, error } = await supabase.rpc('check_patient_terms_acceptance', {
        p_patient_id: patientId
      });

      if (error) throw error;

      return data === true;
    } catch (error) {
      console.error('Error checking patient terms acceptance:', error);
      // Fallback to localStorage on error
      return localStorage.getItem('terms_accepted_patient') === 'true';
    }
  }

  /**
   * Get terms acceptance audit log for a user (for compliance/legal purposes)
   */
  async getUserTermsLog(userId) {
    if (!this.isEnabled) {
      return { success: false, message: 'Database not configured' };
    }

    try {
      const { data, error } = await supabase
        .from('terms_acceptance_log')
        .select('*')
        .eq('user_id', userId)
        .order('accepted_at', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching terms log:', error);
      return { success: false, error };
    }
  }

  /**
   * Get terms acceptance audit log for a patient (for compliance/legal purposes)
   */
  async getPatientTermsLog(patientId) {
    if (!this.isEnabled) {
      return { success: false, message: 'Database not configured' };
    }

    try {
      const { data, error } = await supabase
        .from('terms_acceptance_log')
        .select('*')
        .eq('patient_id', patientId)
        .order('accepted_at', { ascending: false });

      if (error) throw error;

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching patient terms log:', error);
      return { success: false, error };
    }
  }
}

// Export singleton instance
const termsService = new TermsService();
export default termsService;
